import { useMemo } from "react";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { z } from "zod";
import { useLingui } from "@lingui/react/macro";
import { Button } from "@/components/ui/button";
import { helpApi } from "@/lib/api/services";
import {
  HelpCenterHeader,
  HelpToolbar,
  HelpCategoryTabs,
  ArticleCard,
  PopularTopicsCard,
  NeedHelpCard,
  type CategoryFilter,
  type SortOrder,
  type HelpView,
} from "@/components/help-center";

const searchSchema = z.object({
  q: z.string().optional(),
  category: z
    .enum(["all", "getting-started", "guides", "troubleshooting", "whats-new"])
    .catch("all"),
  sort: z.enum(["newest", "oldest"]).catch("newest"),
  view: z.enum(["grid", "list"]).catch("list"),
});

export const Route = createFileRoute("/(protected)/_layout/help-center/")({
  validateSearch: (search) => searchSchema.parse(search),
  loader: async () => ({
    articles: await helpApi.getArticles(),
    popularTopics: await helpApi.getPopularTopics(),
  }),
  component: HelpCenterPage,
});

function HelpCenterPage() {
  const { t } = useLingui();
  const { articles, popularTopics } = Route.useLoaderData();
  const search = useSearch({ from: "/(protected)/_layout/help-center/" });
  const navigate = useNavigate({ from: "/help-center/" });

  const setSearch = (patch: Partial<typeof search>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  const visibleArticles = useMemo(() => {
    const query = (search.q ?? "").trim().toLowerCase();
    return articles
      .filter((a) => search.category === "all" || a.category === search.category)
      .filter(
        (a) =>
          !query ||
          a.title.toLowerCase().includes(query) ||
          a.excerpt.toLowerCase().includes(query),
      )
      .sort((a, b) => {
        const diff =
          new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
        return search.sort === "newest" ? diff : -diff;
      });
  }, [articles, search.category, search.q, search.sort]);

  return (
    <div className="flex flex-col gap-5 py-5 px-3">
      <HelpCenterHeader />

      <div className="flex items-start gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <HelpToolbar
            query={search.q ?? ""}
            onQueryChange={(q) => setSearch({ q: q || undefined })}
            category={search.category}
            onCategoryChange={(category: CategoryFilter) => setSearch({ category })}
            sort={search.sort}
            onSortChange={(sort: SortOrder) => setSearch({ sort })}
            view={search.view}
            onViewChange={(view: HelpView) => setSearch({ view })}
          />

          <HelpCategoryTabs
            value={search.category}
            onChange={(category) => setSearch({ category })}
          />

          {visibleArticles.length === 0 ? (
            <p className="px-1 py-8 text-center text-(--gray-500)">
              {t`No articles match your search.`}
            </p>
          ) : (
            <div
              className={
                search.view === "grid"
                  ? "grid grid-cols-1 gap-3 xl:grid-cols-2"
                  : "flex flex-col gap-3"
              }
            >
              {visibleArticles.map((article) => (
                <ArticleCard
                  key={article.id}
                  article={article}
                  variant={search.view}
                />
              ))}
            </div>
          )}

          <div>
            <Button variant="brand-outline" className="h-13" disabled>
              {t`Load more`}
            </Button>
          </div>
        </div>

        <aside className="hidden w-90 shrink-0 flex-col gap-3 lg:flex">
          <PopularTopicsCard topics={popularTopics} />
          <NeedHelpCard />
        </aside>
      </div>
    </div>
  );
}
